import { useQueries } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { UserData } from 'models/Auth'
import { usePatientPlanType } from 'hooks/usePatientPlanType'

import { TVTVPSIC } from 'settings/_profileSettings'
import { VALIDATED, VALIDATED_WITHOUT_MEDICAL_REQUEST } from 'settings/_creditStatusSettings'
import { PATIENT_SAVED_PLAN, PATIENT_SCHEDULE_STATUS } from 'settings/_storageFlagsSettings'
import { ATTENDING, SCHEDULED } from 'settings/_scheduleStatusSettings'

import { fetchNextSchedules } from 'services/me'
import { INextSchedule } from 'services/me/nextSchedules/types'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { getEnterpriseEligibility } from 'services/enterpriseEligibilityService'
import { getScheduleList } from 'services/schedules'
import { formatScheduleResponse } from 'utils/formatScheduleResponse'

interface UseIsPatientAllowedToSchedule {
  isFirstSession: boolean
  isLoading: boolean,
  isPatientAllowedToSchedule: boolean
  refetchData: () => void
  nextSchedule: INextSchedule
  hasError: boolean
}

function getProfessionalProfileName(value: string) {
  return value === TVTVPSIC ? 'Psicólogo(a)' : 'Médico(a)'
}

function formatSchedule(schedule: INextSchedule) {
  if (!schedule) return null

  return {
    ...schedule,
    professionalProfileRole: getProfessionalProfileName(schedule.professionalProfileCode),
    professionalDocumentValue: `${schedule.professionalDocumentTypeName} ${schedule.professionalDocumentValue}`,
    planName: `${schedule.planGroupName} ${schedule.planName}`,
  }
}

async function getNextSchedule(): Promise<INextSchedule> {
  const [nextSchedule] = await fetchNextSchedules()
  const formattedSchedule = formatSchedule(nextSchedule)
  sessionStorage.setItem(PATIENT_SCHEDULE_STATUS, JSON.stringify(formattedSchedule?.statusName))
  return formattedSchedule
}

async function getPatientEligibility({ username, planCode, isParticular, isCorporate}) {
  if (isParticular) {
    return true
  }

  if (isCorporate) {
    const response = await getCorporateEligibility({
      username,
      planCode
    })
    
    return response?.isEligible
  }

  // Elegibilidade de pacientes enterprise e capitation
  const response = await getEnterpriseEligibility({
    username,
    planCode
  })

  const validStatus = [VALIDATED, VALIDATED_WITHOUT_MEDICAL_REQUEST]
  const requirementsToBeEligible = {
    status: validStatus.includes(response.status),
    availableCredits: response.available_credits > 0,
    expirationDate: new Date(response.end_date) > new Date()
  }
  
  return Object.values(requirementsToBeEligible).every(Boolean)
}

export function useIsPatientAllowedToSchedule(scheduleId: string): UseIsPatientAllowedToSchedule {
  const { isParticular, isCorporate } = usePatientPlanType()
  const user = useSelector((state: UserData) => state.auth)
  const userSelectedPlan = localStorage.getItem(PATIENT_SAVED_PLAN) || user?.planSelected?.partner_plan_code

  const results = useQueries({
    queries: [
      { 
        queryKey: ['PATIENT_NEXT_SCHEDULE', userSelectedPlan],
        queryFn: () => getNextSchedule(),
        refetchOnMount: "always",
      },
      { 
        queryKey: ['PATIENT_ELIGIBILITY', userSelectedPlan],
        queryFn: () => getPatientEligibility({
          username: user?.userData?.username,
          planCode: userSelectedPlan,
          isParticular,
          isCorporate,
        }),
        refetchOnMount: "always",
      },
      {
        queryKey: ['PATIENT_SCHEDULE_LIST', user?.userData?.username],
        queryFn: () => getScheduleList({
          username: user?.userData?.username,
          profileRole: 'PACI',
          endDateGte: new Date().toISOString(),
          statusCode: [SCHEDULED, ATTENDING],
          sort: 'asc'
        }),
        refetchOnMount: "always",
      }
    ]
  })

  const refetchData = () => results.forEach(({ refetch }) => refetch())
  const hasError = results.some(({ error }) => error)
  const isLoading = results.some(({ isLoading, isFetching }) => isLoading || isFetching)

  const nextSchedule = results[0].data
  const isPatientEligible = results[1].data
  const patientSchedules = results[2].data
  const formattedSchedules = formatScheduleResponse(patientSchedules)
  
  const futureSchedules = formattedSchedules.filter(schedule => schedule.statusCode === SCHEDULED)
  const hasFutureSchedules = futureSchedules.length > 0
  const currentSchedule = formattedSchedules.find(schedule => schedule.id === Number(scheduleId))
  const isFirstSession = currentSchedule?.session === null || currentSchedule?.session <= 1
  const isPatientAllowedToSchedule = isPatientEligible && !hasFutureSchedules

  return {
    isLoading,
    isFirstSession,
    refetchData,
    isPatientAllowedToSchedule,
    hasError,
    nextSchedule: nextSchedule || {} as INextSchedule,
  }
}